import useMemoState from '@atlas/tool/box/hooks/useMemoState';
import useRefCallback from 'use-ref-callback';
import React, { useRef } from 'react';

/**
 * Custom hook to replicate behavior of React <input> component state and properties (value, onChange & defaultValue)
 *
 * The default behavior of the <input> component is as follows:
 * - The `value` parameter represents the current value state of the input.
 * - If the `value` parameter is set the input will render this value only even if user attempts to change it via UI.
 * - If `value` is not set, the input will behave as a regular input and will update its value when user changes it.
 * - The `onChange` parameter is a callback function that is called when user attempts to change the value.
 * - The `defaultValue` parameter is used as initial value if `value` was not set at the beginning.
 * - The `defaultValue` is being used only during the initialization (updating this parameter does not affect input state).
 * - The `defaultValue` is being used only if `value` was not set at the beginning (updating `value` to undefined will not restore `defaultValue`).
 *
 * @template T - The type of the state value.
 * @param {T} [state] - The current state value.
 * @param {null | ((newState: T) => void)} [onStateChange] - Callback function to be called when the state changes.
 * @param {T} [defaultState] - The default state value.
 * @returns {TUseControlledState<T>} - An tuple containing the controlled state value and a function to update the state.
 */
export default function useControlledState<T>(
    state?: T,
    onStateChange?: null | ((newState: T) => void),
    defaultState?: T,
): TUseControlledState<T> {
    const lastStateRef = useRef<T>(typeof state === 'undefined' ? (defaultState as T) : state);
    const lastUndefinedRequestTimeRef = useRef<number>(0);

    const [controlledState, setControlledState] = useMemoState<T>(() => {
        if (typeof state !== 'undefined') return state;

        const timeFromLastUndefinedRequest = Date.now() - lastUndefinedRequestTimeRef.current;
        if (timeFromLastUndefinedRequest < TIMEOUT_TO_TREAT_UNDEFINED_AS_EXACT_VALUE) return state as T;
        return lastStateRef.current;
    }, [state]);

    lastStateRef.current = controlledState;

    const set = useRefCallback<React.Dispatch<React.SetStateAction<T>>>((newState) => {
        const calculatedState = typeof newState === 'function' ? (newState as (v: T) => T)(controlledState) : newState;
        if (typeof state === 'undefined') setControlledState(calculatedState);
        lastUndefinedRequestTimeRef.current = typeof calculatedState === 'undefined' ? Date.now() : 0;
        onStateChange?.(calculatedState);
    });

    return [controlledState, set];
}

// #region Helpers

const TIMEOUT_TO_TREAT_UNDEFINED_AS_EXACT_VALUE = 30;

// #endregion

// #region Types

export type TUseControlledState<T> = [state: T, setState: React.Dispatch<React.SetStateAction<T>>];

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, vi, expect } = import.meta.vitest;
    describe('useControlledState', async () => {
        const { renderHook, act } = await import('@testing-library/react');

        it('initiates with passed state', () => {
            const testUndefined = renderHook(() => useControlledState(undefined));
            expect(testUndefined.result.current[0]).toBe(undefined);

            const testNull = renderHook(() => useControlledState(null));
            expect(testNull.result.current[0]).toBe(null);

            const testString = renderHook(() => useControlledState('test'));
            expect(testString.result.current[0]).toBe('test');

            const testObjectState = { test: true };
            const testObject = renderHook(() => useControlledState(testObjectState));
            expect(testObject.result.current[0]).toBe(testObjectState);
        });

        it('updates when state parameter is changed', () => {
            const testUndefinedToNumber = renderHook((state: number | undefined) => useControlledState(state), {
                initialProps: undefined,
            });
            testUndefinedToNumber.rerender(42);
            expect(testUndefinedToNumber.result.current[0]).toBe(42);

            const testNumberToNull = renderHook((state: number | null) => useControlledState(state), {
                initialProps: 42 as number | null,
            });
            testNumberToNull.rerender(null);
            expect(testNumberToNull.result.current[0]).toBe(null);

            const testObjectState = { test: true };
            const testStringToObject = renderHook(
                (state: string | Record<string, unknown>) => useControlledState(state),
                {
                    initialProps: 'test' as string | Record<string, unknown>,
                },
            );
            testStringToObject.rerender(testObjectState);
            expect(testStringToObject.result.current[0]).toBe(testObjectState);
        });

        it('triggers onChange and does not change state if it is set', async () => {
            const onChangeMock = vi.fn();
            const test = renderHook(() => useControlledState<number>(10, onChangeMock));

            expect(onChangeMock).toBeCalledTimes(0);

            await act(() => {
                test.result.current[1](42);
            });
            expect(test.result.current[0]).toBe(10);
            expect(onChangeMock).toBeCalledTimes(1);
            expect(onChangeMock).toBeCalledWith(42);
        });

        it('triggers onChange and does change state if it is not set (undefined)', async () => {
            const onChangeMock = vi.fn();
            const test = renderHook(() => useControlledState<number | undefined>(undefined, onChangeMock));

            expect(onChangeMock).toBeCalledTimes(0);

            await act(() => {
                test.result.current[1](42);
            });
            expect(test.result.current[0]).toBe(42);
            expect(onChangeMock).toBeCalledTimes(1);
            expect(onChangeMock).toBeCalledWith(42);
        });

        it('keeps the last state if state goes undefined, but it resets to undefined if set via fn', () => {
            const test = renderHook((state: number | null | undefined) =>
                useControlledState<number | null | undefined>(state ?? undefined),
            );

            test.rerender(42);
            expect(test.result.current[0]).toBe(42);

            test.rerender(null); // Cannot pass undefined (it will use previous arguments instead), so hacking it with null
            expect(test.result.current[0]).toBe(42);

            test.result.current[1](undefined);
            test.rerender(); // TODO: act() didn't do the work, not sure if the problem is in the code or not understanding how act() works
            expect(test.result.current[0]).toBe(undefined);
        });

        it('uses defaultState if state is undefined, and overrides it with any new state', async () => {
            const testWithInitialState = renderHook(() => useControlledState<number | undefined>(21, null, 42));
            expect(testWithInitialState.result.current[0]).toBe(21);

            const testWithoutInitialState = renderHook((state: number | undefined) =>
                useControlledState<number | undefined>(state, null, 42),
            );
            expect(testWithoutInitialState.result.current[0]).toBe(42);

            testWithoutInitialState.rerender(21);
            expect(testWithoutInitialState.result.current[0]).toBe(21);

            const testWithManualSet = renderHook((state: number | undefined) =>
                useControlledState<number | undefined>(state, null, 42),
            );
            await act(() => {
                testWithManualSet.result.current[1](21);
            });
            expect(testWithManualSet.result.current[0]).toBe(21);
        });

        it('does not restore defaultState even if state goes undefined', () => {
            const test = renderHook((state: number | undefined) =>
                useControlledState<number | undefined>(state, null, 42),
            );

            test.rerender(21);
            expect(test.result.current[0]).toBe(21);

            test.rerender(undefined);
            expect(test.result.current[0]).toBe(21);
        });
    });
}

// #endregion
